/**
 * ErrorBoundary Component
 *
 * A React error boundary that catches JavaScript errors in the component tree
 * and displays a fallback UI instead of crashing the entire application.
 */

import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { useTheme } from '@mui/material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            p: 2
          }}
        >
          <Paper
            elevation={2}
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              maxWidth: 500
            }}
          >
            <Typography variant="h6" color="error" gutterBottom>
              Oops! Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {this.props.fallbackMessage || 'An unexpected error occurred. Please try refreshing the page.'}
            </Typography>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Error: {this.state.error.message}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={this.handleRetry}
              size="small"
              sx={{
                bgcolor: 'var(--primary)',
                '&:hover': {
                  bgcolor: 'rgba(187, 134, 252, 0.9)'
                }
              }}
            >
              Try Again
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;